import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from '../axiosInstance';
import '../pages/banktransfer.css';
import logo from "../assets/logo.png";
import { useCommonVars } from './useCommonVars';

const MAX_SIZE_MB = 50;

const PAYEER_LOGO = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABwAAAAcCAMAAABF0y+mAAAATlBMVEUBqfUApPQAp/VAtfbU7f3O6vy23/tevvcAo/T////C5fzd8f2h1/oAoPQtsfam2fpJufdXvPfp9v78//94x/g3s/YSq/VhwviJ0vpqxfjhyXfuAAAAgklEQVR4Ac3RtQHAMAwEQDNjOPsPGu7y7v0uzySJdBz6hiHjQt5R2vyNafvFGYheKXdpoAhDpCZdyhDm64i5zpYW0mxtZQ2M3tqE3zQmWDsYhOM43rVU1qxz1JQgdNM0VcMIxGBwa1+ks04tzIsuaxNL2njCIxMT3esKHyX3Z+7VcU7OGgZFJm19pAAAAABJRU5ErkJggg==";

function PayeerTransferPage({ user }) {
  const [copiedMessage, setCopiedMessage] = useState('');
  const [details, setDetails] = useState(null);
  const [errorMsgPay, setErrorMsgPay] = useState('');
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();
  const location = useLocation();
  const amount = location.state?.amount ?? 0;

  const [uploadedFile, setUploadedFile] = useState(null);
  const [showUploadError, setShowUploadError] = useState(false);
  const [uploadErrorMsg, setUploadErrorMsg] = useState('');
  const [highlight, setHighlight] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [blockActions, setBlockActions] = useState(false);

  const uploadRef = useRef();
  const fileInputRef = useRef();
  const actionsRef = useRef();
  const fallbackInputRef = useRef();
  const addedIosRef = useRef(false);
  const addedPushRef = useRef(false);

  const { PAYEER, supportUrl } = useCommonVars(user);

  useEffect(() => {
    const ua = navigator.userAgent || "";
    const isIphone = /iPhone|iPod/.test(ua);
    if (isIphone) {
      document.documentElement.classList.add('ios');
      addedIosRef.current = true;
    } else {
      document.documentElement.classList.add('push-2cm');
      addedPushRef.current = true;
    }
    document.documentElement.classList.add('av-hidden');
    return () => {
      document.documentElement.classList.remove('av-hidden');
      if (addedIosRef.current) document.documentElement.classList.remove('ios');
      if (addedPushRef.current) document.documentElement.classList.remove('push-2cm');
    };
  }, []);

  useEffect(() => {
    setErrorMsgPay('');
    setDetails({
      amount,
      card_number: PAYEER || '-',
      pay_id: `peer_${Date.now()}`
    });
    setLoading(false);
  }, [amount, PAYEER]);

  const copy = async (text, label) => {
    const val = String(text || '');
    let copied = false;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(val);
        copied = true;
      }
    } catch {}
    if (!copied) {
      try {
        const textarea = document.createElement('textarea');
        textarea.value = val;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        textarea.style.pointerEvents = 'none';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        textarea.blur();
        document.body.removeChild(textarea);
        copied = true;
      } catch {}
    }
    if (!copied) {
      if (fallbackInputRef.current) {
        fallbackInputRef.current.value = val;
        fallbackInputRef.current.style.display = 'block';
        fallbackInputRef.current.focus();
        fallbackInputRef.current.select();
        setCopiedMessage('Текст выделен, скопируйте вручную');
        setTimeout(() => {
          fallbackInputRef.current.style.display = 'none';
          fallbackInputRef.current.blur();
          document.activeElement.blur();
          setCopiedMessage('');
        }, 3200);
        return;
      }
      setCopiedMessage('Скопируйте вручную');
      setTimeout(() => setCopiedMessage(''), 2200);
      return;
    }
    setCopiedMessage(`${label} скопировано`);
    setTimeout(() => {
      setCopiedMessage('');
      document.activeElement.blur();
    }, 1700);
  };

  const goBack = () => {
    if (blockActions || submitting) return;
    setBlockActions(true);
    navigate('/card');
    setTimeout(() => setBlockActions(false), 800);
  };

  const handleFileSelect = file => {
    if (!file) return;
    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      setUploadedFile(null);
      setUploadErrorMsg(`Файл слишком большой (максимум ${MAX_SIZE_MB} МБ)`);
      setShowUploadError(true);
      setHighlight(true);
      setTimeout(() => setHighlight(false), 1600);
      setTimeout(() => setShowUploadError(false), 3500);
      setTimeout(() => {
        if (uploadRef.current) {
          uploadRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
      return;
    }
    setUploadedFile(file);
    setUploadErrorMsg('');
    setShowUploadError(false);
    setHighlight(false);
    setTimeout(() => {
      if (actionsRef.current) {
        actionsRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 150);
  };

  const handleConfirm = async () => {
    if (blockActions || submitting) return;
    if (!uploadedFile) {
      setHighlight(true);
      setShowUploadError(true);
      setUploadErrorMsg('Пожалуйста, прикрепите скрин/файл подтверждения перевода');
      setTimeout(() => setHighlight(false), 1600);
      setTimeout(() => setShowUploadError(false), 3500);
      setTimeout(() => {
        if (uploadRef.current) {
          uploadRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
      return;
    }
    setBlockActions(true);
    setHighlight(false);
    setShowUploadError(false);
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('user_id', user?.telegram_id);
      formData.append('pay_id', details.pay_id);
      formData.append('method', 'peer');
      formData.append('amount', details.amount);
      formData.append('file', uploadedFile);
      const res = await axios.post('/project/send-payment-message', formData, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      if (res.data?.status === "ok") {
        navigate('/card', { state: { confirmMessage: "✅ Заявка отправлена. Ожидайте подтверждения администратора." } });
      } else {
        alert("❌ Не удалось отправить заявку. Повторите позже.");
      }
    } catch {
      alert("⚠️ Ошибка при подтверждении. Попробуйте позже.");
    } finally {
      setSubmitting(false);
      setBlockActions(false);
    }
  };

  const uploadSection = (
    <div ref={uploadRef} className={`upload-section${highlight ? ' upload-section-highlight' : ''}`}>
      {showUploadError && (
        <div className="upload-error-block">
          <div className="upload-error-text">
            <svg style={{width:22,height:22,marginRight:6}} viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="10" fill="#fecaca"/>
              <path d="M10 6v4" stroke="#dc2626" strokeWidth="2" strokeLinecap="round"/>
              <circle cx="10" cy="14" r="1.3" fill="#dc2626"/>
            </svg>
            {uploadErrorMsg || 'Ошибка загрузки файла'}
          </div>
          <div className="upload-error-arrow">
            <svg width="28" height="28" viewBox="0 0 28 28">
              <path d="M14 6v13m0 0-6.5-6.5M14 19l6.5-6.5" stroke="#dc2626" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
        </div>
      )}
      <label className="upload-label" style={{ width: "100%", display: "block" }}>
        Загрузите скрин/файл перевода:
        <input
          type="file"
          ref={fileInputRef}
          accept="image/*,application/pdf"
          style={{ display: 'none' }}
          onChange={e => handleFileSelect(e.target.files[0])}
        />
        <div
          className="upload-dropzone"
          tabIndex={0}
          onDrop={e => { e.preventDefault(); handleFileSelect(e.dataTransfer.files[0]); }}
          onDragOver={e => e.preventDefault()}
          onClick={() => fileInputRef.current && fileInputRef.current.click()}
          onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { fileInputRef.current && fileInputRef.current.click(); } }}
        >
          <div style={{ width: "100%" }}>
            <button
              type="button"
              className="btn-confirm2"
              style={{ width: 195, fontSize: 15, margin: "0 auto 6px auto", display: "block", background: "#8B5CF6" }}
              tabIndex={-1}
              onClick={e => { e.stopPropagation(); fileInputRef.current && fileInputRef.current.click(); }}
            >
              {uploadedFile ? "Заменить файл" : "Выбрать файл"}
            </button>
            <div style={{ color: "#A5B4FC", fontSize: "14px", opacity: 0.95, marginTop: 3 }}>
              или перетащите сюда файл
            </div>
          </div>
        </div>
      </label>
      {uploadedFile && (
        <div className="upload-file-info" style={{ marginTop: 10 }}>
          {uploadedFile.type.startsWith('image/')
            ? <img src={URL.createObjectURL(uploadedFile)} alt="preview" className="upload-preview" style={{ maxWidth: 160, maxHeight: 120, marginBottom: 8 }} />
            : <div className="upload-filename">{uploadedFile.name}</div>
          }
          <div>
            <span style={{ fontSize: 13, color: "#ccc" }}>({(uploadedFile.size / 1024 / 1024).toFixed(2)} МБ)</span>
            <button className="upload-remove-btn" style={{ marginLeft: 10 }} onClick={() => setUploadedFile(null)}>Удалить</button>
          </div>
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="loader-center">
        <div className="loader-wrapper">
          <div className="loader-logo-wrapper1">
            <img src={logo} alt="Logo" className="loader-logo" />
          </div>
          <div className="glow-circle"></div>
          <div className="loading-text">
            Получаем реквизиты для оплаты
            <span className="dot-animate">
              <span>.</span>
              <span>.</span>
              <span>.</span>
            </span>
          </div>
          <div className="loader-hint">
            Обычно занимает не&nbsp;более 30&nbsp;секунд
          </div>
        </div>
      </div>
    );
  }

  if (errorMsgPay) {
    return (
      <div className="bank-transfer-container">
        <div className="error-msg-pay">
          {errorMsgPay}
          <div className="error-msg-hint">
            Попробуйте изменить сумму или способ оплаты.
          </div>
          <div className="error-msg-support">
            Или&nbsp;<a href={supportUrl} style={{ color: '#7C3AED', textDecoration: 'underline' }}>обратитесь в поддержку</a>
          </div>
        </div>
        <div className="transfer-actions" style={{ marginTop: 30 }}>
          <button className="btn-cancel2" onClick={goBack} disabled={blockActions || submitting}>Назад</button>
        </div>
      </div>
    );
  }

  if (!details) return null;

  return (
    <div className="bank-transfer-container">
      <div className="method-header">
        <span className="method-logo">
          <img
            src={PAYEER_LOGO}
            alt="Payeer"
            width={28}
            height={28}
            loading="lazy"
            decoding="async"
            style={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }}
          />
        </span>
        <span className="method-title">Оплата через Payeer</span>
      </div>
      {copiedMessage && <div className="copy-toast">{copiedMessage}</div>}
      <input ref={fallbackInputRef} readOnly style={{ position: 'fixed', top: '-60px', left: 0, opacity: 0, zIndex: 9999, fontSize: 16, pointerEvents: 'none', display: 'none' }} />
      <div className="transfer-tip">
        Скопируйте кошелёк Payeer и отправьте перевод одним платежом.
        <div className="transfer-deadline">
          Время на оплату: 20 минут.
        </div>
      </div>
      <div className="transfer-field">
        <label>Номер (Payeer):</label>
        <div className="transfer-row">
          <span>{details.card_number || '-'}</span>
          <button className="copy-btn" onClick={() => copy(details.card_number, 'Номер')}>Скопировать</button>
        </div>
      </div>
      <div className="transfer-field">
        <label>Сумма перевода:</label>
        <div className="transfer-row">
          <span>{details.amount || '-'}</span>
          <button className="copy-btn" onClick={() => copy(details.amount, 'Сумма')}>Скопировать</button>
        </div>
      </div>
      {uploadSection}
      <div className="transfer-actions" ref={actionsRef}>
        <button className="btn-cancel2" onClick={goBack} disabled={blockActions || submitting}>Отмена</button>
        <button className="btn-confirm2" onClick={handleConfirm} disabled={submitting || loading || blockActions}>
          {submitting ? "Отправка..." : "Подтвердить"}
        </button>
      </div>
    </div>
  );
}

export default PayeerTransferPage;
